import { YStack, XStack, Text, styled } from 'tamagui'
import { Card, Button, GradientText } from '../ui'
import { copy } from '../../content/copy'

const Section = styled(YStack, {
  name: 'ValueStackSection',
  paddingHorizontal: '$6',
  paddingVertical: '$16',
  backgroundColor: '$backgroundSecondary',

  '$md': {
    paddingHorizontal: '$4',
    paddingVertical: '$10',
  },
})

const Container = styled(YStack, {
  name: 'Container',
  maxWidth: 800,
  marginHorizontal: 'auto',
  width: '100%',
  gap: '$8',
})

const Header = styled(YStack, {
  name: 'Header',
  gap: '$3',
  marginBottom: '$4',
})

const HeaderRow = styled(XStack, {
  name: 'HeaderRow',
  alignItems: 'flex-start',
  gap: '$4',
})

const SectionNumber = styled(Text, {
  name: 'SectionNumber',
  fontFamily: '$accent',
  fontSize: '$2',
  color: '$textMuted',
  letterSpacing: 2,
})

const SectionTitle = styled(Text, {
  name: 'SectionTitle',
  fontFamily: '$accent',
  fontSize: '$3',
  color: '$orangePrimary',
  letterSpacing: 2,
  textTransform: 'uppercase',
})

const Subtitle = styled(Text, {
  name: 'Subtitle',
  // @ts-ignore - Tamagui's `render` controls the underlying HTML element on web
  render: 'h2',
  fontFamily: '$heading',
  fontSize: '$7',
  fontWeight: '600',
  color: '$textPrimary',
  marginTop: '$4',

  '$md': {
    fontSize: '$6',
  },
})

const ValueList = styled(YStack, {
  name: 'ValueList',
  gap: '$1',
})

const ValueItem = styled(XStack, {
  name: 'ValueItem',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  paddingVertical: '$4',
  borderBottomWidth: 1,
  borderBottomColor: '$border',
})

const ValueContent = styled(YStack, {
  name: 'ValueContent',
  flexGrow: 1,
  flexShrink: 1,
  minWidth: 0,
  gap: '$1',
})

const ValueName = styled(Text, {
  name: 'ValueName',
  fontFamily: '$body',
  fontSize: '$5',
  fontWeight: '500',
  color: '$textPrimary',
})

const ValueDescription = styled(Text, {
  name: 'ValueDescription',
  fontFamily: '$body',
  fontSize: '$3',
  color: '$textSecondary',
})

const ValuePrice = styled(Text, {
  name: 'ValuePrice',
  fontFamily: '$mono',
  fontSize: '$4',
  color: '$textMuted',
  textDecorationLine: 'line-through',
})

const TotalSection = styled(YStack, {
  name: 'TotalSection',
  gap: '$4',
  paddingTop: '$6',
})

const TotalRow = styled(XStack, {
  name: 'TotalRow',
  justifyContent: 'space-between',
  alignItems: 'center',
})

const TotalLabel = styled(Text, {
  name: 'TotalLabel',
  fontFamily: '$body',
  fontSize: '$5',
  fontWeight: '500',
  color: '$textSecondary',
})

const TotalValue = styled(Text, {
  name: 'TotalValue',
  fontFamily: '$mono',
  fontSize: '$5',
  color: '$textMuted',
  textDecorationLine: 'line-through',
})

const YourPriceRow = styled(XStack, {
  name: 'YourPriceRow',
  justifyContent: 'space-between',
  alignItems: 'center',
  backgroundColor: 'rgba(255, 99, 0, 0.1)',
  padding: '$4',
  borderRadius: '$4',
  marginTop: '$2',
})

const YourPriceLabel = styled(Text, {
  name: 'YourPriceLabel',
  fontFamily: '$heading',
  fontSize: '$6',
  fontWeight: '600',
  color: '$textPrimary',
})

const AnchorText = styled(Text, {
  name: 'AnchorText',
  fontFamily: '$body',
  fontSize: '$4',
  color: '$textSecondary',
  textAlign: 'center',
  fontStyle: 'italic',
  marginTop: '$4',
})

interface ValueStackSectionProps {
  onCtaClick?: () => void
}

export function ValueStackSection({ onCtaClick }: ValueStackSectionProps) {
  const { sectionNumber, sectionTitle, subtitle, items, totalValue, yourPrice, anchor } =
    copy.valueStack

  return (
    <Section id="diagnosis">
      <Container>
        <Header>
          <HeaderRow>
            <SectionNumber>{sectionNumber}</SectionNumber>
            <SectionTitle>{sectionTitle}</SectionTitle>
          </HeaderRow>
          <Subtitle>{subtitle}</Subtitle>
        </Header>

        <Card variant="elevated" size="lg">
          <ValueList>
            {items.map((item, index) => (
              <ValueItem key={index}>
                <ValueContent>
                  <ValueName>{item.name}</ValueName>
                  <ValueDescription>{item.description}</ValueDescription>
                </ValueContent>
                <ValuePrice>{item.value}</ValuePrice>
              </ValueItem>
            ))}
          </ValueList>

          <TotalSection>
            <TotalRow>
              <TotalLabel>Total Value</TotalLabel>
              <TotalValue>{totalValue}</TotalValue>
            </TotalRow>

            <YourPriceRow>
              <YourPriceLabel>Your Price</YourPriceLabel>
              <Text fontFamily="$heading" fontSize="$8" fontWeight="700">
                <GradientText>{yourPrice}</GradientText>
              </Text>
            </YourPriceRow>
          </TotalSection>

          <Button
            variant="primary"
            size="xl"
            fullWidth
            marginTop="$6"
            onPress={onCtaClick}
          >
            Schedule Diagnosis
          </Button>

          <AnchorText>{anchor}</AnchorText>
        </Card>
      </Container>
    </Section>
  )
}
